import React, { FC, useState } from "react";
import { HiPlus } from "react-icons/hi2";
import { Link } from "react-router-dom";

import { useGetClientPortalQuery } from "../../slices/ClientPortalSlice";
import { portalSummary } from "./clientPortal.utils";
import { InviteToPortalModal } from "./InviteToPortalModal";

/**
 * Client profile Files widget header (owner 2026-10-28, Loom d6af3122): the
 * old "Share with client" button is gone (a file's own share icon already
 * shares it). Instead: "Invite to portal" while nobody has portal access, a
 * green "Portal active" once someone signed in, "Invited to portal" while the
 * invitation is still open. The last two open the Client portal tab's People.
 */
export const PortalStatusButton: FC<{ customerId: string }> = ({
  customerId,
}) => {
  const { data } = useGetClientPortalQuery(
    { customerId },
    { skip: !customerId },
  );
  const [open, setOpen] = useState(false);
  if (!data) return null;
  const state = portalSummary(
    (data.people ?? []).filter((p) => p.status !== "revoked"),
  );

  if (state !== "none")
    return (
      <Link
        to={"../portal?view=people"}
        relative={"path"}
        data-testid={"portal-status"}
        className={
          state === "active"
            ? "inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#E9F9F1] px-2.5 text-[12.5px] font-bold text-[#147A4B] hover:bg-[#DDF4E8]"
            : "inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#FFF4DB] px-2.5 text-[12.5px] font-bold text-[#8A5A00] hover:bg-[#FCEBC4]"
        }>
        <span
          className={
            state === "active"
              ? "h-1.5 w-1.5 rounded-full bg-[#22A06B]"
              : "h-1.5 w-1.5 rounded-full bg-[#D99A00]"
          }
        />
        {state === "active" ? "Portal active" : "Invited to portal"}
      </Link>
    );

  return (
    <>
      <button
        type={"button"}
        data-testid={"portal-status"}
        onClick={() => setOpen(true)}
        disabled={!!data.archived}
        title={
          data.archived
            ? "This client is archived. Unarchive it to invite people."
            : "Invite this client's contacts to sign in to their portal"
        }
        className={
          "inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#E4E7EC] bg-white px-2.5 text-[12.5px] font-bold text-[#3A4256] hover:bg-[#F4F2FF] disabled:cursor-not-allowed disabled:opacity-50"
        }>
        <HiPlus size={14} /> Invite to portal
      </button>
      {open && !data.archived && (
        <InviteToPortalModal overview={data} onClose={() => setOpen(false)} />
      )}
    </>
  );
};
